import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  appointmentDetailDescription,
  appointmentDetailTitle,
  appointmentEventColorClass,
  bookingSourceLabel,
} from '../lib/appointmentDisplay';
import { isAppointmentAttendanceWindowOpen } from '../lib/clinicTime';
import { CheckCircle, CircleCheck, FolderOpen, UserX, XCircle } from 'lucide-react';

const ATTENDANCE_ACTION_TITLE = 'Disponible el día y a la hora de la cita';

export default function AppointmentGridDetailCard({
  cell,
  gridDateYmd,
  now,
  onConfirm,
  onCancel,
  onComplete,
  onClinicalHistory,
  onNoShow,
}) {
  if (!cell?.appointment_id) return null;

  const status = String(cell.status || 'pending').toLowerCase();
  const patientName = String(cell.patient_name || '').trim();
  const timeLabel = String(cell.time || '').trim();
  const canMarkAttendance =
    (status === 'pending' || status === 'confirmed') &&
    isAppointmentAttendanceWindowOpen(gridDateYmd, cell.time, now);

  return (
    <div
      className={`rounded-lg border p-2.5 text-left ${appointmentEventColorClass(cell)}`}
      data-testid={`grid-appt-${cell.appointment_id}`}
    >
      <div className="flex items-start justify-between gap-1.5">
        <p className="font-semibold text-xs leading-snug">
          {appointmentDetailTitle(patientName, cell.appointment_type_label)}
        </p>
        <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0 h-5 font-normal">
          {bookingSourceLabel(cell.booking_source)}
        </Badge>
      </div>
      {timeLabel ? <p className="text-xs mt-1 opacity-90">{timeLabel}</p> : null}
      <p className="text-xs text-muted-foreground mt-1 leading-snug">
        {appointmentDetailDescription(cell)}
      </p>
      <div className="mt-2 flex flex-wrap gap-1">
        {status === 'pending' ? (
          <>
            <Button
              type="button"
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={() => onConfirm?.(cell.appointment_id)}
            >
              <CheckCircle className="w-3 h-3 mr-1" />
              Confirmar
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 px-2 text-[11px]"
              onClick={() => onCancel?.(cell.appointment_id, patientName)}
            >
              <XCircle className="w-3 h-3 mr-1" />
              Cancelar
            </Button>
          </>
        ) : null}
        {status === 'confirmed' ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[11px]"
            onClick={() => onCancel?.(cell.appointment_id, patientName)}
          >
            <XCircle className="w-3 h-3 mr-1" />
            Cancelar
          </Button>
        ) : null}
        {status === 'pending' || status === 'confirmed' ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-7 px-2 text-[11px]"
            disabled={!canMarkAttendance}
            title={!canMarkAttendance ? ATTENDANCE_ACTION_TITLE : undefined}
            onClick={() => onComplete?.(cell.appointment_id)}
          >
            <CircleCheck className="w-3 h-3 mr-1" />
            Marcar completada
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2 text-[11px] gap-1"
          onClick={() => onClinicalHistory?.(cell.appointment_id, patientName)}
        >
          <FolderOpen className="w-3 h-3" />
          Historia clínica
        </Button>
        {status === 'pending' || status === 'confirmed' ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[11px] gap-1 border-amber-500/50 text-amber-900 dark:text-amber-200"
            disabled={!canMarkAttendance}
            title={!canMarkAttendance ? ATTENDANCE_ACTION_TITLE : undefined}
            onClick={() => onNoShow?.(cell.appointment_id, patientName)}
          >
            <UserX className="w-3 h-3" />
            No llegó paciente
          </Button>
        ) : null}
      </div>
    </div>
  );
}
